import Modal from "components/DesignSystem/Modal/Modal";
import { Form, Formik, FormikValues } from "formik";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import {
  LaunchFilingPayload,
  PAYMENT_SCHEDULE,
  useGetLaunchFilingStateQuery,
  useLaunchFilingMutation,
} from "store/apis/filing";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { SingleValue } from "react-select";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import dayjs, { localeData } from "dayjs";
import { addFilingSchema } from "formValidations/addFilingSchema";
import { getSeasonsFrom } from "utils/getAllSeason";

dayjs.extend(localeData);

const quertelyMap = new Map<string, { from: string; to: string }>([
  ["Q1", { from: "January", to: "March" }],
  ["Q2", { from: "April", to: "June" }],
  ["Q3", { from: "July", to: "September" }],
  ["Q4", { from: "October", to: "December" }],
]);

const getFilingPeriodFormatted = ({
  filingSchedule,
  filingPeriod,
}: {
  filingSchedule: string;
  filingPeriod: string[];
}) => {
  if (filingSchedule === PAYMENT_SCHEDULE.MONTHLY) {
    return filingPeriod.map((month) => ({ from: month, to: null }));
  }
  if (filingSchedule === PAYMENT_SCHEDULE.QUARTERLY) {
    return filingPeriod.map(
      (quarter) => quertelyMap.get(quarter) || { from: "", to: "" }
    );
  }
  if (filingSchedule === PAYMENT_SCHEDULE.SEMI_ANNUALLY) {
    return [
      {
        from: "January",
        to: "June",
      },
      {
        from: "June",
        to: "December",
      },
    ];
  }
  return [
    {
      from: "January",
      to: "December",
    },
  ];
};

const quartelyOptions = [
  { label: "Q1: Jan-Mar", value: "Q1" },
  { label: "Q2: Apr-Jun", value: "Q2" },
  { label: "Q3: Jul-Sep", value: "Q3" },
  { label: "Q4: Oct-Dec", value: "Q4" },
];

const semiAnnuallyOptions = [
  { label: "Jan-Jun", value: "Jan-Jun" },
  { label: "Jul-Dec", value: "Jul-Dec" },
];

const scheduleMap = new Map<string, string>([
  ["MONTHLY", "Monthly"],
  ["QUARTERLY", "Quarterly"],
  ["SEMI_ANNUALLY", "Semi annually"],
  ["ANNUALLY", "Annually"],
]);

export const AddFilingModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const entityId = useCurrentEntityId();

  const { data, isLoading, isSuccess } = useGetLaunchFilingStateQuery(
    { entityId },
    { skip: !entityId }
  );
  const [launchFiling, { isLoading: isLaunchingFiling }] =
    useLaunchFilingMutation();

  const { alertToast, successToast } = useToast();

  const stateOptions = data?.map((state) => ({
    label: state.name,
    value: state.uuid,
  }));
  const seasonOptions = getSeasonsFrom(2020).map((year) => ({
    label: year.toString(),
    value: year.toString(),
  }));

  const monthlyOptions = dayjs.months().map((month) => ({
    label: month,
    value: month,
  }));

  const filingPeriodOptions = {
    MONTHLY: monthlyOptions,
    QUARTERLY: quartelyOptions,
    SEMI_ANNUALLY: semiAnnuallyOptions,
  };

  const createFiling = async (values: FormikValues) => {
    const { filing_period, payment_schedule, filing_season, ...restValues } =
      values;

    const filingPeriod = getFilingPeriodFormatted({
      filingSchedule: payment_schedule,
      filingPeriod: filing_period,
    });

    const payload = {
      ...restValues,
      filing_periods: filingPeriod,
      filing_schedule: payment_schedule,
      filing_season: dayjs(filing_season).year(),
    };
    const selectedState = stateOptions?.find(
      ({ value }) => value === values.state
    );
    try {
      await launchFiling({
        entityId,
        stateId: values.state,
        payload: payload as LaunchFilingPayload,
      }).unwrap();

      successToast({
        message: `${filingPeriod.length} Filings launched for ${selectedState?.label}`,
      });
      onClose();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  return (
    <Modal.Root open={open} onOpenChange={onClose}>
      <Modal.Content useCustomOverlay>
        <Modal.Header>
          <Modal.Title>Add Filing </Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Async.Root isEmpty={false} isLoading={isLoading} isSuccess={isSuccess}>
          <Async.Success>
            <Formik
              validationSchema={addFilingSchema}
              initialValues={{
                state: "",
                filing_season: "",
                payment_schedule: null,
                filing_period: [],
              }}
              validateOnBlur
              onSubmit={createFiling}
            >
              {({ setFieldValue, values }) => {
                const handleStateChange = (option: SingleValue<OptionData>) => {
                  if (option) {
                    setFieldValue("state", option.value);

                    const filingSchedule = data?.find(
                      ({ uuid }) => uuid === option.value
                    )?.payment_schedule;

                    setFieldValue("payment_schedule", filingSchedule);
                  }
                };

                return (
                  <Form>
                    <Modal.Body>
                      <div className="t-flex t-gap-y-3 t-flex-col t-h-full">
                        <div className="t-grid t-grid-cols-2 t-gap-x-2">
                          <Combobox
                            withForm
                            size="regular"
                            label="State"
                            required
                            name="state"
                            onChange={(option) => {
                              handleStateChange(
                                option as SingleValue<OptionData>
                              );
                            }}
                            menuPortalTarget={document.getElementById("root")}
                            options={stateOptions}
                            isClearable={false}
                          />

                          <Combobox
                            withForm
                            size="regular"
                            label="Filing season"
                            required
                            name="filing_season"
                            menuPortalTarget={document.body}
                            options={seasonOptions}
                            isClearable={false}
                          />
                        </div>
                        <div>
                          <p className="t-text-text-30 t-text-body-sm t-m-0">
                            Filing schedule
                          </p>
                          <span className="t-text-text-100 t-text-body-sm t-capitalize">
                            {(values.payment_schedule &&
                              scheduleMap.get(values.payment_schedule)) ||
                              "-"}
                          </span>
                        </div>
                        {values.payment_schedule === "ANNUALLY" ? (
                          <div>
                            <p className="t-text-text-30 t-text-body-sm t-m-0">
                              Filing period
                            </p>
                            <span className="t-text-text-100 t-text-body-sm">
                              Jan-Dec
                            </span>
                          </div>
                        ) : (
                          <Combobox
                            withForm
                            size="regular"
                            label="Filing period"
                            name={`filing_period`}
                            isClearable={false}
                            required
                            menuPortalTarget={document.body}
                            isMulti
                            options={
                              values.payment_schedule
                                ? filingPeriodOptions[
                                    values.payment_schedule
                                  ] || []
                                : []
                            }
                          />
                        )}
                      </div>
                    </Modal.Body>
                    <Modal.FooterButtonGroup>
                      <Button onClick={onClose}>Cancel</Button>
                      <Button
                        type="submit"
                        isLoading={isLaunchingFiling}
                        customType="primary"
                      >
                        Add
                      </Button>
                    </Modal.FooterButtonGroup>
                  </Form>
                );
              }}
            </Formik>
          </Async.Success>
        </Async.Root>
      </Modal.Content>
    </Modal.Root>
  );
};
